import { API } from "@/main.js";

const getDefaultState = () => {
  return {
    home: {
      projection: "4326",
      sidebar: {
        enabled: true,
        openSidebar: "",
        openTools: false
      },
      mainMap: {
        style: localStorage.getItem("map_type"),
        objectToZoom: undefined
      },
      tools: {
        selectedAction: "",
        selectedObjectLayer: "",
        selectedTechnicalType: ""
      },
      activeLayout: 1,
      tabs: {
        active: "home"
      },

      forceWorkspaceFilter: true,

      measurementsToolbar: {
        show: false,
        selectedTool: ""
      },

      buildings: [],

      showTabNetworkLayers: false,

      print: false,

      showHeatmapToolbar: false,

      fullscreen: false,

      geomObjectNetworkToCenter: null
    }
  };
};

const state = getDefaultState();

const getters = {
  isHomeSidebarEnabled: state => state.home.sidebar.enabled,
  getCurrentlyOpenHomeSidebar: state => state.home.sidebar.openSidebar,
  isHomeToolsSidebarOpen: state => state.home.sidebar.openTools,
  getCurrentMainMapStyle: state => state.home.mainMap.style,
  tools_selectedAction: state => state.home.tools.selectedAction,
  isObjectToolbarOpen: state => state.home.tools.selectedAction === "create",
  isToolActionActive: state => state.home.tools.selectedAction !== "",
  tools_selectedObjectLayer: state => state.home.tools.selectedObjectLayer,
  tools_selectedTechnicalType: state => state.home.tools.selectedTechnicalType,
  getActiveLayoutMode: state => state.home.activeLayout,
  getActiveFibercloudTab: state => state.home.tabs.active,
  getObjectToZoomGeoJson: state => state.home.mainMap.objectToZoom,
  getSelectedProjection: state => state.home.projection,
  isMeasurementsToolbarOpen: state => state.home.measurementsToolbar.show,
  getSelectedMeasurementTool: state =>
    state.home.measurementsToolbar.selectedTool,
  getForceWorkspaceFilter: state => state.home.forceWorkspaceFilter,
  isShowTabNetworkLayers: state => state.home.showTabNetworkLayers,
  isToTakePrint: state => state.home.print,
  isShowHeatmap: state => state.home.showHeatmapToolbar,
  isFullscreen: state => state.home.fullscreen,
  getGeomObjectNetworkToCenter: state => state.home.geomObjectNetworkToCenter,
  getSpfBuildings: state => state.home.buildings
};

const mutations = {
  RESET_HOME_STORE_MODULE_STATE(state) {
    Object.assign(state, getDefaultState());
  },
  TOGGLE_SIDEBAR(state, mapIsActive) {
    state.home.sidebar.enabled = mapIsActive;
  },
  SET_MAP_STYLE(state, style) {
    state.home.mainMap.style = style;
    localStorage.setItem("map_type", style);
  },
  SET_OPEN_HOME_SIDEBAR(state, sidebarName) {
    state.home.sidebar.openSidebar = sidebarName;
  },
  SET_HOME_TOOLS_SIDEBAR_STATE(state, isOpen) {
    state.home.sidebar.openTools = isOpen;
  },

  SET_HOME_PRINT_STATE(state, isOpen) {
    state.home.print = isOpen;
  },

  SET_TOOL_ACTION(state, action) {
    state.home.tools.selectedAction = action;
  },
  SET_TOOL_OBJECT_LAYER(state, layer) {
    state.home.tools.selectedObjectLayer = layer;
  },
  SET_TOOL_TECHNICAL_TYPE(state, type) {
    state.home.tools.selectedTechnicalType = type;
  },
  SET_ACTIVE_FIBERCLOUD_TAB(state, tab) {
    state.home.tabs.active = tab;
  },
  SET_OBJECT_TO_ZOOM_ON_MAP(state, geoJson) {
    state.home.mainMap.objectToZoom = geoJson;
  },
  SET_ACTIVE_PROJECTION(state, projection) {
    state.home.projection = projection;
  },
  UPDATE_ACTIVE_LAYOUT_MODE(state, layoutMode) {
    state.home.activeLayout = layoutMode;
  },
  SET_MEASUREMENTS_TOOLBAR_STATE(state, newState) {
    state.home.measurementsToolbar.show = newState;
  },
  ADD_BUILDINGS_SPF(state, newState) {
    state.home.buildings = [...state.home.buildings, newState];
  },
  DELETE_BUILDING_SPF(state, id) {
    state.home.buildings = state.home.buildings.filter(building => building.id !== id);
  },
  DELETE_ALL_SPF(state, newState) {
    state.home.buildings = [];
  },
  SET_SELECTED_MEASUREMENT_TOOL(state, selectedTool) {
    state.home.measurementsToolbar.selectedTool = selectedTool;
  },
  SET_FORCE_WORKSPACE_FILTER(state, forceWorkspaceFilter) {
    state.home.forceWorkspaceFilter = forceWorkspaceFilter;
  },

  SET_SHOW_TAB_NETWORK_LAYERS(state, isOpen) {
    state.home.showTabNetworkLayers = isOpen;
  },

  SET_SHOW_HEATMAP(state, isOpen) {
    state.home.showHeatmapToolbar = isOpen;
  },

  SET_FULLSCREEN(state, isFullscreen) {
    state.home.fullscreen = isFullscreen;
  },

  CENTER_OBJECT_NETWORK_ON_WORKSPACE(state, geom) {
    state.home.geomObjectNetworkToCenter = geom;
  }
};

const actions = {
  addBuildingsSPF({ commit }, newState) {
    commit("ADD_BUILDINGS_SPF", newState);
  },
  deleteBuildingSPF({ commit }, newState) {
    commit("DELETE_BUILDING_SPF", newState);
  },
  deleteAllSPF({ commit }, newState) {
    commit("DELETE_ALL_SPF", newState);
  },
  RESET_HOME_STORE_MODULE_STATE({ commit }) {
    commit("RESET_HOME_STORE_MODULE_STATE");
    commit("RESET_NETWORK_DIAGRAM_STATE");
    commit("RESET_FORMS_MODULE_STATE");
  },

  SET_OBJECT_TO_ZOOM_ON_MAP({ commit }, geoJson) {
    commit("SET_OBJECT_TO_ZOOM_ON_MAP", geoJson);
  },

  toggleSidebar({ commit }, mapIsActive) {
    commit("TOGGLE_SIDEBAR", mapIsActive);
  },

  SET_MAP_STYLE({ commit }, style) {
    commit("SET_MAP_STYLE", style);
  },

  setOpenHomeSidebar({ commit }, sidebarName) {
    commit("SET_OPEN_HOME_SIDEBAR", sidebarName);
  },

  setHomeToolsSidebarState({ commit }, action) {
    commit("SET_HOME_TOOLS_SIDEBAR_STATE", action);
  },

  setHomePrintState({ commit }, action) {
    commit("SET_HOME_PRINT_STATE", action);
  },

  SET_TOOL_ACTION({ commit }, action) {
    commit("SET_TOOL_ACTION", action);
  },

  SET_TOOL_OBJECT_LAYER({ commit }, type) {
    commit("SET_TOOL_OBJECT_LAYER", type);
    if (type === "") {
      commit("SET_TOOL_TECHNICAL_TYPE", "");
    }
  },

  SET_TOOL_TECHNICAL_TYPE({ commit }, type) {
    commit("SET_TOOL_TECHNICAL_TYPE", type);
  },

  SET_ACTIVE_FIBERCLOUD_TAB({ commit }, tab) {
    commit("SET_ACTIVE_FIBERCLOUD_TAB", tab);
  },
  SET_ACTIVE_PROJECTION({ commit }, projection) {
    commit("SET_ACTIVE_PROJECTION", projection);
  },
  SET_ACTIVE_LAYOUT_MODE({ commit }, layoutMode) {
    commit("UPDATE_ACTIVE_LAYOUT_MODE", layoutMode);
  },
  setMeasurementsToolbarState({ commit }, newState) {
    commit("SET_MEASUREMENTS_TOOLBAR_STATE", newState);
  },
  setSelectedMeasureTool({ commit }, selectedTool) {
    commit("SET_SELECTED_MEASUREMENT_TOOL", selectedTool);
  },

  fetchImageFromGeoServer(
    { commit },
    { params, workspaceUUID = null, token, isNetwork = false }
  ) {
    return new Promise((resolve, reject) => {
      API.geo_server.getImage(
        params,
        workspaceUUID,
        token,
        isNetwork,
        result => {
          resolve(result);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  fetchFeaturesFromGeoServer({ commit }, { data, isNetwork = false }) {
    return new Promise((resolve, reject) => {
      API.geo_server.getData(
        data,
        isNetwork,
        result => {
          resolve(result.data);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  setForceWorkspaceFilter({ commit }, data) {
    commit("SET_FORCE_WORKSPACE_FILTER", data);
  },

  setShowTabNetworkLayers({ commit }, data) {
    commit("SET_SHOW_TAB_NETWORK_LAYERS", data);
  },

  setShowHeatmap({ commit }, data) {
    commit("SET_SHOW_HEATMAP", data);
  },

  setFullscreen({ commit }, data) {
    if (data) {
      if (
        document.fullscreenElement === null ||
        !(document.mozFullscreen && document.webkitIsFullscreen)
      ) {
        commit("SET_FULLSCREEN", true);
        if (document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen();
        } else if (document.documentElement.mozRequestFullscreen) {
          document.documentElement.mozRequestFullscreen();
        } else if (document.documentElement.webkitRequestFullscreen) {
          document.documentElement.webkitRequestFullscreen(
            Element.ALLOW_KEYBOARD_INPUT
          );
        }
      }
    } else {
      if (
        document.fullscreenElement ||
        (document.mozFullscreen && document.webkitIsFullscreen)
      ) {
        commit("SET_FULLSCREEN", false);
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.webkitCancelFullScreen) {
          document.webkitCancelFullScreen();
        }
      }
    }
  },

  centerObjectNetworkOnWorkspace({ commit }, geom) {
    commit("CENTER_OBJECT_NETWORK_ON_WORKSPACE", geom);
  },

  /**
   * Executes the method to create the tech points
   *
   * @param {Object} data - the data to send to the backend
   */
  createBulkTPs({ commit }, data) {
    return new Promise((resolve, reject) => {
      API.networkObject.createBulkTechPoints(
        data,
        result => {
          resolve(result.data);
        },
        error => {
          reject(error);
        }
      )
    })
  }
};

export default { state, getters, actions, mutations };



// WEBPACK FOOTER //
// ./src/store/modules/Home/home.js